import React, { useState, useRef, useEffect, useCallback } from 'react';
import ChatMessage from './ChatMessage';
import './ChatDrawer.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// SHELVED 2026-07-22: follow-up question chips suggested nonsensical/unanswerable
// questions in production. Flip back to true once suggestion quality improves.
// Disables both the async /api/insight/followups fetch and chip rendering
// (including refusal-derived follow_ups). Code paths kept intact.
const FOLLOW_UPS_ENABLED = false;

// Username from the stored JWT (payload.sub) — display/gating only, the
// backend independently verifies the token on every request.
function currentUsername() {
  try {
    const token = sessionStorage.getItem('nexus_token');
    return JSON.parse(atob(token.split('.')[1])).sub || null;
  } catch { return null; }
}

const TAB_LABELS = {
  network: 'Network',
  heatmap: 'Landscape',
  umap: 'Topic Clusters',
  data: 'Data',
  summary: 'Summary',
};

// Human-readable progress labels for server-sent stage events.
const STAGE_LABELS = {
  routing: 'Deciding where to look…',
  answering: 'Reading the current view…',
  searching: 'Searching documents…',
};

// Suggested starter questions per tab (shown in empty state).
// Mix of analytics (📊) and document-retrieval (📄) questions so users
// can see both engines working and quickly spot routing failures.
const SUGGESTED_QUESTIONS = {
  heatmap: [
    { q: 'Which RI clusters have the broadest topic coverage?', icon: '📊' },
    { q: 'What do the documents say about transnational access eligibility?', icon: '📄' },
    { q: 'Are there topic gaps — topics with very few RI clusters?', icon: '📊' },
    { q: 'How is FAIR data management described in the corpus?', icon: '📄' },
  ],
  umap: [
    { q: 'Which topic clusters are most spatially isolated?', icon: '📊' },
    { q: 'What language does the TNA/proposals cluster use around access?', icon: '📄' },
    { q: 'Which clusters sit closest to the FAIR/data-management cluster?', icon: '📊' },
    { q: 'What do documents in the personal data cluster say about GDPR?', icon: '📄' },
  ],
  network: [
    { q: 'Which organisations are the most central in this network?', icon: '📊' },
    { q: 'What best practices are described for transnational access user selection?', icon: '📄' },
    { q: 'Are there bridge nodes connecting separate communities?', icon: '📊' },
  ],
  summary: [
    { q: 'What are the dominant topics in this dataset?', icon: '📊' },
    { q: 'Summarise how Horizon Europe access schemes differ from H2020.', icon: '📄' },
    { q: 'Which RI clusters have the most documents?', icon: '📊' },
  ],
};

/**
 * ChatDrawer — persistent collapsible right-side drawer for contextual chat.
 * Inherits current tab, filters, and selected nodes from the parent app.
 */
export default function ChatDrawer({
  isOpen,
  onToggle,
  tab,
  filters,
  topicView,
  selectedNodes,
  authFetch,
  onOpenDocument,
  isFront = true,
  onFocus,
}) {
  const [messages, setMessages] = useState(() => {
    // Restore the transcript across reloads (per browser tab).
    try {
      const saved = JSON.parse(sessionStorage.getItem('chatMessages'));
      return Array.isArray(saved) ? saved : [];
    } catch { return []; }
  });
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  // Progress text shown next to the typing dots (from server stage events).
  const [loadingStatus, setLoadingStatus] = useState('');
  // Intro starts open only until the user has seen it once.
  const [introOpen, setIntroOpen] = useState(() => localStorage.getItem('chatIntroSeen') !== '1');
  // Drawer width (px) — user-resizable via the left-edge handle, persisted.
  const [width, setWidth] = useState(() => {
    const saved = parseInt(localStorage.getItem('chatDrawerWidth'), 10);
    return Number.isFinite(saved) ? saved : 420;
  });
  const [liveSuggestions, setLiveSuggestions] = useState(null);
  // contextTabOverride: set when "Ask about this" fires from a sub-section
  // (e.g. UMAP insight card while outer tab is 'heatmap'/Landscape)
  const contextTabOverrideRef = useRef(null);
  const [sectionLabel, setSectionLabel] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  // In-flight request controller — lets the Stop button cancel generation.
  const abortRef = useRef(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const sessionIdRef = useRef(
    sessionStorage.getItem('chatSessionId')
    || `session_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
  );

  // Keep refs to latest prop values so callbacks don't go stale
  const tabRef = useRef(tab);
  const topicViewRef = useRef(topicView);
  const filtersRef = useRef(filters);
  const selectedNodesRef = useRef(selectedNodes);
  tabRef.current = tab;
  topicViewRef.current = topicView;
  filtersRef.current = filters;
  selectedNodesRef.current = selectedNodes;

  useEffect(() => {
    // Only auto-scroll when the user is already near the bottom — never yank
    // them down while they're re-reading an earlier answer.
    const el = messagesContainerRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 160;
    if (nearBottom) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Persist transcript + session id so a reload doesn't wipe the conversation.
  useEffect(() => {
    try {
      sessionStorage.setItem('chatMessages', JSON.stringify(messages.slice(-40)));
      sessionStorage.setItem('chatSessionId', sessionIdRef.current);
    } catch { /* storage full/unavailable — non-fatal */ }
  }, [messages]);

  // Drag the left edge to resize. Clamped to [360, 90vw]; width persisted.
  const startResize = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = width;
    const maxWidth = window.innerWidth * 0.9;
    const onMove = (moveEvt) => {
      const next = Math.min(maxWidth, Math.max(360, startWidth + (startX - moveEvt.clientX)));
      setWidth(next);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      setWidth((w) => { localStorage.setItem('chatDrawerWidth', String(Math.round(w))); return w; });
    };
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [width]);

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 300);
  }, [isOpen]);

  // Fetch view-derived starter questions when the empty state is shown.
  // These name real topics from the current filtered view so they always return
  // something relevant; falls back to the static SUGGESTED_QUESTIONS on failure.
  useEffect(() => {
    if (!isOpen || messages.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${API_BASE_URL}/api/insight/suggestions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tab: tabRef.current,
            filters: filtersRef.current,
            view: topicViewRef.current || undefined,
            selected_nodes: selectedNodesRef.current ? Array.from(selectedNodesRef.current) : [],
          }),
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data.suggestions) && data.suggestions.length > 0) {
          setLiveSuggestions(data.suggestions);
        }
      } catch {
        /* keep static fallback */
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, tab, filters, topicView, messages.length, authFetch]);

  // Ask for follow-up suggestions AFTER the answer has rendered, so the extra
  // LLM round-trip never delays the answer itself. Patches the message in place.
  const fetchFollowUps = useCallback((msgId, query, result) => {
    if (!FOLLOW_UPS_ENABLED) return;
    if (!result || result.refused || result.error) return;
    if (Array.isArray(result.follow_ups) && result.follow_ups.length > 0) return;
    authFetch(`${API_BASE_URL}/api/insight/followups`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, answer: (result.answer || '').slice(0, 4000) }),
    })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (d && Array.isArray(d.follow_ups) && d.follow_ups.length > 0) {
          setMessages(prev => prev.map(m => (m.id === msgId ? { ...m, followUps: d.follow_ups } : m)));
        }
      })
      .catch(() => { /* best-effort */ });
  }, [authFetch]);

  const sendMessage = useCallback(async (messageText, tabOverride, opts = {}) => {
    const text = (messageText || input).trim();
    if (!text || sending) return;

    const { forceRoute = null, skipUserBubble = false } = opts;

    // Consume the one-shot section override (set by askAboutInsight)
    const effectiveTab = contextTabOverrideRef.current || tabOverride || tabRef.current;
    contextTabOverrideRef.current = null;
    setSectionLabel(null);

    if (!skipUserBubble) {
      const userMessage = { role: 'user', content: text, ts: Date.now() };
      setMessages(prev => [...prev, userMessage]);
    }
    setInput('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    setSending(true);
    setLoadingStatus('Thinking…');
    // First question sent — the intro has served its purpose.
    setIntroOpen(false);
    localStorage.setItem('chatIntroSeen', '1');

    const msgId = `m_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const controller = new AbortController();
    abortRef.current = controller;
    const payload = {
      message: text,
      tab: effectiveTab,
      filters: filtersRef.current,
      view: topicViewRef.current || undefined,
      selected_nodes: selectedNodesRef.current ? Array.from(selectedNodesRef.current) : [],
      session_id: sessionIdRef.current,
      ...(forceRoute ? { force_route: forceRoute } : {}),
    };

    // Insert (first call) or update (subsequent calls) this exchange's assistant bubble.
    const upsertAssistant = (patch) => {
      setMessages(prev => {
        const idx = prev.findIndex(m => m.id === msgId);
        if (idx === -1) {
          return [...prev, { id: msgId, role: 'assistant', content: '', query: text, ts: Date.now(), ...patch }];
        }
        const next = [...prev];
        next[idx] = { ...next[idx], ...patch };
        return next;
      });
    };

    const finalize = (data) => {
      upsertAssistant({
        content: data.answer || 'No response received.',
        streaming: false,
        engine: data.engine,
        sources: data.sources,
        claims: data.claims,
        coverage: data.coverage,
        refused: data.refused,
        error: !!data.error,
        classification: data.classification,
        rewrittenQuery: data.rewritten_query,
        followUps: data.follow_ups,
        answerParts: data.answer_parts,
      });
      fetchFollowUps(msgId, text, data);
    };

    let streamedAny = false;
    try {
      // Streaming endpoint: stage events while working, token deltas for
      // analytics answers, one final `done` event with the full result.
      const response = await authFetch(`${API_BASE_URL}/api/insight/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      if (!response.ok || !response.body) throw new Error(`stream unavailable (${response.status})`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let acc = '';
      let finalResult = null;

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split('\n\n');
        buffer = frames.pop();
        for (const frame of frames) {
          const line = frame.split('\n').find(l => l.startsWith('data: '));
          if (!line) continue;
          let ev;
          try { ev = JSON.parse(line.slice(6)); } catch { continue; }
          if (ev.event === 'stage') {
            setLoadingStatus(STAGE_LABELS[ev.stage] || 'Working…');
          } else if (ev.event === 'delta') {
            acc += ev.text;
            streamedAny = true;
            upsertAssistant({ content: acc, streaming: true });
          } else if (ev.event === 'done') {
            finalResult = ev.result;
          } else if (ev.event === 'error') {
            throw new Error(ev.message || 'Chat failed');
          }
        }
      }

      if (!finalResult) throw new Error('Stream ended unexpectedly');
      finalize(finalResult);
    } catch (err) {
      if (streamedAny) {
        // A partial answer is already on screen — close it out rather than erroring.
        upsertAssistant({ streaming: false, engine: 'context' });
      } else if (err.name === 'AbortError') {
        upsertAssistant({ content: 'Stopped before an answer arrived.', engine: null, streaming: false, error: true });
      } else {
        // Fall back to the non-streaming endpoint (proxy without SSE support, etc.).
        try {
          const response = await authFetch(`${API_BASE_URL}/api/insight/chat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            signal: controller.signal,
          });
          if (!response.ok) {
            const errBody = await response.json().catch(() => ({}));
            throw new Error(errBody.error || `Request failed: ${response.status}`);
          }
          finalize(await response.json());
        } catch (err2) {
          const msg = err2.name === 'AbortError' ? 'Stopped before an answer arrived.' : `Error: ${err2.message}`;
          upsertAssistant({ content: msg, engine: null, streaming: false, error: true });
        }
      }
    } finally {
      abortRef.current = null;
      setSending(false);
      setLoadingStatus('');
    }
  }, [input, sending, authFetch, fetchFollowUps]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Fire-and-forget thumbs up/down vote on an assistant answer.
  const sendFeedback = useCallback((msg, rating, comment) => {
    authFetch(`${API_BASE_URL}/api/insight/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        rating,
        comment: comment || '',
        query: msg.query || '',
        answer: (msg.content || '').slice(0, 4000),
        engine: msg.engine || '',
        tab: tabRef.current,
        session_id: sessionIdRef.current,
      }),
    }).catch(() => { /* best-effort */ });
  }, [authFetch]);

  // Called from insight cards: "Ask about this" or a directed follow-up chip.
  // contextTab is the sub-section that owns the card (e.g. 'umap' even when outer tab is 'heatmap').
  // directQuestion, when present, is a specific follow-up question to send verbatim.
  const askAboutInsight = useCallback((title, body, contextTab, directQuestion) => {
    if (contextTab) {
      contextTabOverrideRef.current = contextTab;
      setSectionLabel(TAB_LABELS[contextTab] || contextTab);
    }
    onToggle(true);
    const prompt = directQuestion && directQuestion.trim()
      ? directQuestion.trim()
      : `Tell me more about this insight: "${title}" — ${body}`;
    setTimeout(() => sendMessage(prompt, contextTab), 350);
  }, [onToggle, sendMessage]);

  useEffect(() => {
    window.__chatDrawerAskAbout = askAboutInsight;
    return () => { delete window.__chatDrawerAskAbout; };
  }, [askAboutInsight]);

  // Clear the conversation and start a fresh session (so server-side history,
  // if any, also resets for the next question). Two-click inline confirm —
  // no blocking window.confirm dialog.
  const handleClear = useCallback(() => {
    if (messages.length > 0 && !confirmClear) {
      setConfirmClear(true);
      setTimeout(() => setConfirmClear(false), 3000);
      return;
    }
    setConfirmClear(false);
    setMessages([]);
    contextTabOverrideRef.current = null;
    setSectionLabel(null);
    sessionIdRef.current = `session_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }, [messages.length, confirmClear]);

  // Export the transcript as a readable Markdown file for sharing/verification.
  // Includes engine route and full citations per assistant turn.
  const handleExport = useCallback(() => {
    if (messages.length === 0) return;
    const ENGINE_NAMES = {
      rag: 'Documents (RAG)',
      context: 'Analytics',
      context_fallback: 'Analytics (RAG failed)',
      both: 'Analytics + Documents',
    };
    const lines = [
      '# Nexus chat transcript',
      '',
      `- Session: ${sessionIdRef.current}`,
      `- Exported: ${new Date().toISOString()}`,
      `- Context: ${TAB_LABELS[tabRef.current] || tabRef.current}`,
      '',
      '---',
      '',
    ];
    for (const m of messages) {
      const when = m.ts ? new Date(m.ts).toISOString() : '';
      if (m.role === 'user') {
        lines.push(`## You${when ? ` · ${when}` : ''}`, '', m.content, '');
      } else {
        const eng = m.engine ? (ENGINE_NAMES[m.engine] || m.engine) : 'assistant';
        lines.push(`## Assistant — ${eng}${when ? ` · ${when}` : ''}`, '', m.content, '');
        if (m.coverage && m.coverage.total_claims != null) {
          const c = m.coverage;
          lines.push(
            `_Verification: ${c.verified_claims}/${c.total_claims} claims verified · ` +
            `${c.verified_sources}/${c.total_sources} sources verified${c.transparency ? ` · ${c.transparency}` : ''}_`,
            ''
          );
        }
        if (Array.isArray(m.claims) && m.claims.length > 0) {
          const sym = { verified: '[verified]', unverified: '[unverified]', not_found: '[not found]' };
          lines.push('**Claims:**', '');
          m.claims.forEach((cl, i) => {
            lines.push(`${i + 1}. ${sym[cl.trust] || '[?]'} ${cl.text}`);
          });
          lines.push('');
        }
        if (Array.isArray(m.sources) && m.sources.length > 0) {
          lines.push(`**Sources (${m.sources.length}):**`, '');
          m.sources.forEach((s, i) => {
            const loc = s.source_locator || {};
            const acro = s.project_acronym ? `${s.project_acronym}${s.framework_programme ? ` (${s.framework_programme})` : ''}` : null;
            const name = acro || (s.project_id ? `Project ${s.project_id}` : (loc.file || s.chunk_id || 'source'));
            const para = loc.para_idx != null ? ` ¶${loc.para_idx + 1}` : '';
            const url = s.source_url ? ` <${s.source_url}>` : '';
            const trust = s.trust ? ` [${s.trust}]` : '';
            const excerpt = (s.text || '').slice(0, 240).replace(/\s+/g, ' ').trim();
            lines.push(`${i + 1}. ${name}${para}${trust}${url}`);
            if (loc.file) lines.push(`   file: ${loc.file}`);
            if (excerpt) lines.push(`   "${excerpt}${(s.text || '').length > 240 ? '…' : ''}"`);
          });
          lines.push('');
        }
      }
      lines.push('---', '');
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    a.href = url;
    a.download = `nexus-chat-${stamp}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [messages]);

  const tabLabel = TAB_LABELS[tab] || tab;
  const isAdmin = (currentUsername() || '').toLowerCase().startsWith('admin');

  // Admin-only: download all collected chat feedback as CSV.
  const handleFeedbackDownload = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE_URL}/api/insight/feedback?limit=500`);
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      const data = await res.json();
      const cols = ['ts', 'user', 'session_id', 'tab', 'rating', 'engine', 'query', 'answer', 'comment'];
      const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const rows = [cols.join(','), ...(data.recent || []).map(e => cols.map(c => esc(e[c])).join(','))];
      const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nexus-chat-feedback-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      window.alert(`Could not download feedback: ${err.message}`);
    }
  }, [authFetch]);

  const suggestions = (liveSuggestions && liveSuggestions.length > 0)
    ? liveSuggestions
    : (SUGGESTED_QUESTIONS[tab] || SUGGESTED_QUESTIONS.heatmap);

  return (
    <>
      <button className="chat-drawer-toggle" onClick={() => onToggle(!isOpen)} title="Open chat">
        {isOpen ? (
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="4" y1="4" x2="14" y2="14"/><line x1="14" y1="4" x2="4" y2="14"/></svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2L14.4 8.8H21.6L15.6 13.2L18 20L12 15.6L6 20L8.4 13.2L2.4 8.8H9.6Z"/>
          </svg>
        )}
      </button>

      <div
        className={`chat-drawer-overlay${isOpen ? ' open' : ''}${isFront ? ' chat-front' : ''}`}
        style={{ width }}
        onMouseDown={onFocus}
      >
        <div
          className="chat-drawer-resize-handle"
          onMouseDown={startResize}
          title="Drag to resize"
          role="separator"
          aria-orientation="vertical"
        />
        <div className="chat-drawer-header">
          <h3>Ask about your data</h3>
          <span className="chat-drawer-context-badge">
            {sectionLabel ? `${tabLabel} · ${sectionLabel}` : tabLabel}
          </span>
          <div className="chat-drawer-header-actions">
            <button
              className={`chat-drawer-action chat-drawer-action-danger${confirmClear ? ' confirming' : ''}`}
              onClick={handleClear}
              disabled={messages.length === 0}
              title="Clear conversation"
            >{confirmClear ? 'Sure?' : 'Clear'}</button>
            <button
              className="chat-drawer-action"
              onClick={handleExport}
              disabled={messages.length === 0}
              title="Export transcript (Markdown)"
            >Export</button>
            {isAdmin && (
              <button
                className="chat-drawer-action"
                onClick={handleFeedbackDownload}
                title="Download collected chat feedback (CSV)"
              >Feedback</button>
            )}
          </div>
          <button className="chat-drawer-close" onClick={() => onToggle(false)}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="2" y1="2" x2="12" y2="12"/><line x1="12" y1="2" x2="2" y2="12"/></svg>
          </button>
        </div>

        <div className="chat-drawer-intro">
          <button
            className="chat-drawer-intro-toggle"
            onClick={() => setIntroOpen(o => {
              const next = !o;
              if (!next) localStorage.setItem('chatIntroSeen', '1');
              return next;
            })}
            aria-expanded={introOpen}
          >
            <span>How this works</span>
            <span className="chat-drawer-intro-caret">{introOpen ? '▾' : '▸'}</span>
          </button>
          {introOpen && (
            <div className="chat-drawer-intro-body">
              <p>
                Ask in plain language — each question is routed to the right source automatically:
              </p>
              <ul>
                <li>
                  <span className="chat-intro-tag tag-data">📊 Analytics</span>
                  structural questions (counts, sizes, distances, what's largest or
                  closest) are answered from the <strong>{tabLabel}</strong> view you're looking at now.
                </li>
                <li>
                  <span className="chat-intro-tag tag-docs">📄 Documents</span>
                  content questions (what the texts actually say, policies, practices,
                  wording) search the <strong>document corpus</strong> and return cited passages.
                </li>
              </ul>
              <p className="chat-drawer-intro-note">
                Your current filters scope both. Answers from documents show a verification
                badge and per-claim ✓/✗ checks so you can confirm them against the sources.
              </p>
            </div>
          )}
        </div>

        <div className="chat-drawer-messages" ref={messagesContainerRef}>
          {messages.length === 0 ? (
            <div className="chat-drawer-empty">
              <div className="chat-drawer-empty-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary-teal)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L14.4 8.8H21.6L15.6 13.2L18 20L12 15.6L6 20L8.4 13.2L2.4 8.8H9.6Z"/>
                </svg>
              </div>
              <div>Ask questions about what you see in the dashboard.</div>
              <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 12 }}>
                Try one of these to see both sources in action:
              </div>
              <div className="chat-suggestions">
                {suggestions.map(({ q, icon }, i) => (
                  <button key={i} className="chat-suggestion-chip" onClick={() => sendMessage(q)}>
                    <span className="chat-suggestion-icon">{icon}</span>{q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((msg, idx) => (
              <ChatMessage
                key={msg.id || idx}
                message={msg}
                isLast={idx === messages.length - 1}
                onOpenDocument={onOpenDocument}
                onRerun={(route) => sendMessage(msg.query, null, { forceRoute: route, skipUserBubble: true })}
                onFollowUp={FOLLOW_UPS_ENABLED ? (q) => sendMessage(q) : null}
                onFeedback={(rating, comment) => sendFeedback(msg, rating, comment)}
                onRetry={() => sendMessage(msg.query, null, { skipUserBubble: true })}
              />
            ))
          )}
          {sending && !(messages.length > 0 && messages[messages.length - 1].streaming) && (
            <div className="chat-message-loading">
              <div className="chat-typing-dots"><span /><span /><span /></div>
              {loadingStatus && <span className="chat-loading-status">{loadingStatus}</span>}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="chat-drawer-input-area">
          <textarea
            ref={inputRef}
            className="chat-drawer-input"
            placeholder="Ask a question…"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              // Auto-grow with content, capped so it never eats the transcript.
              e.target.style.height = 'auto';
              e.target.style.height = `${Math.min(e.target.scrollHeight, 140)}px`;
            }}
            onKeyDown={handleKeyDown}
            rows={1}
          />
          {sending ? (
            <button
              className="chat-drawer-send chat-drawer-stop"
              onClick={() => abortRef.current?.abort()}
              title="Stop generating"
            >
              Stop
            </button>
          ) : (
            <button
              className="chat-drawer-send"
              onClick={() => sendMessage()}
              disabled={!input.trim()}
            >
              Send
            </button>
          )}
        </div>
        <div className="chat-drawer-disclaimer">
          AI-generated &mdash; may contain errors. Please verify.
        </div>
      </div>
    </>
  );
}

